import i18n from "../i18n";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
    Cog, ClipboardCheck, Plus, Trash2, PencilLine, Save,
    Gauge, Wrench, AlertTriangle, Activity, Package, Timer, Boxes,
} from "lucide-react";
import { toast } from "sonner";
import { formatCurrency, formatNumber } from "@/lib/format";
import { farmDateIso } from "@/lib/farmTime";
import {
    LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";

const EQ_TYPES = ["machinery", "tool", "electrical", "plumbing", "other"];
const EQ_STATUS = [
    { key: "in_use", label: "Operating", color: "bg-primary/15 text-primary" },
    { key: "idle", label: "Idle / Available", color: "bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200" },
    { key: "in_repair", label: "Under Repair", color: "bg-amber-500/15 text-amber-700" },
    { key: "retired", label: "Retired", color: "bg-secondary text-muted-foreground" },
];
const MAINT_KINDS = [
    { key: "service", label: "Service" },
    { key: "repair", label: "Repair" },
    { key: "inspection", label: "Inspection" },
    { key: "other", label: "Other" },
];

const statusMeta = (k) => EQ_STATUS.find((s) => s.key === (k || "in_use")) || EQ_STATUS[0];
const submissionId = (prefix) => `${prefix}-${farmDateIso().replace(/-/g, "")}-${crypto.randomUUID()}`;

function KpiCard({ icon: Icon, label, value, sub, tone = "primary", testid }) {
    const tones = {
        primary: "bg-primary/10 text-primary",
        amber: "bg-amber-500/15 text-amber-700 dark:text-amber-300",
        rose: "bg-rose-500/15 text-rose-700 dark:text-rose-300",
        emerald: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300",
        slate: "bg-slate-500/15 text-slate-700 dark:text-slate-300",
        blue: "bg-blue-500/15 text-blue-700 dark:text-blue-300",
    };
    return (
        <Card className="p-4" data-testid={testid}>
            <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl grid place-items-center ${tones[tone]}`}>
                    <Icon className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="text-xs uppercase tracking-wider text-muted-foreground truncate">{label}</div>
                    <div className="text-xl font-bold leading-tight">{value}</div>
                </div>
            </div>
            {sub && <div className="text-[11px] text-muted-foreground mt-2">{sub}</div>}
        </Card>
    );
}

// ---------- Equipment Dialog ----------
function EquipmentDialog({ open, initial, categories, employees, onSave, onClose }) {
    const [draft, setDraft] = useState({});
    useEffect(() => {
        if (open) setDraft({ status: "in_use", type: "machinery", source_submission_id: initial?.source_submission_id || submissionId("equipment"), ...(initial || {}) });
    }, [open, initial?.id]);
    const upd = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="equipment-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Cog className="w-5 h-5" />{draft.id ? "Edit Equipment" : "New Equipment"}
                    </DialogTitle>
                </DialogHeader>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    <div className="col-span-2">
                        <Label className="text-xs">{i18n.t("final.dbb3ca9ccf55")}</Label>
                        <Input value={draft.name ?? ""} onChange={(e) => upd("name", e.target.value)} className="mt-1 h-9" data-testid="eq-name" />
                    </div>
                    <div>
                        <Label className="text-xs">{i18n.t("final.a3c686e711e4")}</Label>
                        <Select value={draft.category || "__none__"} onValueChange={(v) => upd("category", v === "__none__" ? "" : v)}>
                            <SelectTrigger className="mt-1 h-9" data-testid="eq-category"><SelectValue placeholder={i18n.t("final.c3b69e661eec")} /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="__none__">{i18n.t("final.f187d5a1cc2f")}</SelectItem>
                                {categories.map((c) => <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="text-xs">{i18n.t("final.3deb74565196")}</Label>
                        <Select value={draft.type || "machinery"} onValueChange={(v) => upd("type", v)}>
                            <SelectTrigger className="mt-1 h-9" data-testid="eq-type"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {EQ_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="text-xs">{i18n.t("final.bae7d5be7082")}</Label>
                        <Select value={draft.status || "in_use"} onValueChange={(v) => upd("status", v)}>
                            <SelectTrigger className="mt-1 h-9" data-testid="eq-status"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {EQ_STATUS.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    {[
                        ["brand", "Brand"], ["model", "Model"], ["serial_no", "Serial Number"],
                        ["engine_no", "Engine Number"], ["purchase_cost", "Purchase Cost (₱)", "number"],
                    ].map(([k, l, t]) => (
                        <div key={k}>
                            <Label className="text-xs">{l}</Label>
                            <Input type={t || "text"} value={draft[k] ?? ""} className="mt-1 h-9"
                                   onChange={(e) => upd(k, t === "number" ? Number(e.target.value) || null : e.target.value)} />
                        </div>
                    ))}
                    <div>
                        <Label className="text-xs">{i18n.t("final.7658feb298a0")}</Label>
                        <Select value={draft.assigned_operator_id || "__none__"} onValueChange={(v) => upd("assigned_operator_id", v === "__none__" ? null : v)}>
                            <SelectTrigger className="mt-1 h-9" data-testid="eq-operator"><SelectValue placeholder="—" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="__none__">{i18n.t("final.c3ab24576f62")}</SelectItem>
                                {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    {[
                        ["purchase_date", "Purchase Date", "date"],
                        ["warranty_until", "Warranty Until", "date"],
                        ["engine_hours", "Engine Hours", "number"],
                        ["next_due_hours", "Next Service @ Hours", "number"],
                        ["next_service_date", "Next Service Date", "date"],
                        ["location", "Current Location"],
                    ].map(([k, l, t]) => (
                        <div key={k}>
                            <Label className="text-xs">{l}</Label>
                            <Input type={t || "text"} value={draft[k] ?? ""} className="mt-1 h-9"
                                   onChange={(e) => upd(k, t === "number" ? Number(e.target.value) || null : e.target.value)} />
                        </div>
                    ))}
                </div>
                <div>
                    <Label className="text-xs">{i18n.t("final.70440046a3dc")}</Label>
                    <Textarea rows={2} value={draft.notes ?? ""} onChange={(e) => upd("notes", e.target.value)} className="mt-1" />
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>{i18n.t("final.77dfd2135f4d")}</Button>
                    <Button onClick={() => onSave(draft)} disabled={!draft.name} data-testid="eq-save-btn">
                        <Save className="w-4 h-4 mr-2" />{i18n.t("final.efc007a393f6")}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// ---------- Engine Hours Dialog ----------
function HoursDialog({ open, equipment, onSave, onClose }) {
    const [draft, setDraft] = useState({});
    useEffect(() => {
        if (open) setDraft({ date: farmDateIso(), source_submission_id: submissionId("equipment-hours") });
    }, [open]);
    const upd = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-lg" data-testid="hours-dialog">
                <DialogHeader><DialogTitle className="flex items-center gap-2"><Timer className="w-5 h-5" />{i18n.t("final.4729f4827a28")}</DialogTitle></DialogHeader>
                <div className="space-y-3">
                    <div>
                        <Label className="text-xs">{i18n.t("final.36155d48f460")}</Label>
                        <Select value={draft.equipment_id || ""} onValueChange={(v) => upd("equipment_id", v)}>
                            <SelectTrigger className="mt-1 h-10" data-testid="hours-equipment"><SelectValue placeholder={i18n.t("final.c3b69e661eec")} /></SelectTrigger>
                            <SelectContent>
                                {equipment.map((e) => <SelectItem key={e.id} value={e.id}>{e.name} {e.category ? `· ${e.category}` : ""}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="text-xs">{i18n.t("final.eb9a4bc1c0c1")}</Label>
                            <Input type="date" value={draft.date || ""} onChange={(e) => upd("date", e.target.value)} className="mt-1 h-10" data-testid="hours-date" />
                        </div>
                        <div>
                            <Label className="text-xs">{i18n.t("final.67f0a1936718")}</Label>
                            <Input type="number" step="0.1" value={draft.hours_reading ?? ""} onChange={(e) => upd("hours_reading", Number(e.target.value) || 0)} className="mt-1 h-10" data-testid="hours-reading" />
                        </div>
                    </div>
                    <div>
                        <Label className="text-xs">{i18n.t("final.70440046a3dc")}</Label>
                        <Textarea rows={2} value={draft.notes ?? ""} onChange={(e) => upd("notes", e.target.value)} className="mt-1" />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>{i18n.t("final.77dfd2135f4d")}</Button>
                    <Button onClick={() => onSave(draft)} disabled={!draft.equipment_id || !draft.hours_reading} data-testid="hours-save-btn">
                        <Save className="w-4 h-4 mr-2" />{i18n.t("final.efc007a393f6")}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// ---------- Maintenance Dialog ----------
function MaintDialog({ open, equipment, onSave, onClose }) {
    const [draft, setDraft] = useState({});
    useEffect(() => {
        if (open) setDraft({ date: farmDateIso(), kind: "service", source_submission_id: submissionId("equipment-maintenance") });
    }, [open]);
    const upd = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-lg" data-testid="eq-maint-dialog">
                <DialogHeader><DialogTitle className="flex items-center gap-2"><Wrench className="w-5 h-5" />{i18n.t("final.24f02ec6aa87")}</DialogTitle></DialogHeader>
                <div className="space-y-3">
                    <div>
                        <Label className="text-xs">{i18n.t("final.36155d48f460")}</Label>
                        <Select value={draft.equipment_id || ""} onValueChange={(v) => upd("equipment_id", v)}>
                            <SelectTrigger className="mt-1 h-10" data-testid="eq-maint-equipment"><SelectValue placeholder={i18n.t("final.c3b69e661eec")} /></SelectTrigger>
                            <SelectContent>
                                {equipment.map((e) => <SelectItem key={e.id} value={e.id}>{e.name} {e.category ? `· ${e.category}` : ""}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="text-xs">{i18n.t("final.3deb74565196")}</Label>
                            <Select value={draft.kind || "service"} onValueChange={(v) => upd("kind", v)}>
                                <SelectTrigger className="mt-1 h-10" data-testid="eq-maint-kind"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {MAINT_KINDS.map((k) => <SelectItem key={k.key} value={k.key}>{k.label}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-xs">{i18n.t("final.eb9a4bc1c0c1")}</Label>
                            <Input type="date" value={draft.date || ""} onChange={(e) => upd("date", e.target.value)} className="mt-1 h-10" data-testid="eq-maint-date" />
                        </div>
                        <div>
                            <Label className="text-xs">{i18n.t("final.b13bdb89caf8")}</Label>
                            <Input type="number" value={draft.cost ?? ""} onChange={(e) => upd("cost", Number(e.target.value) || 0)} className="mt-1 h-10" data-testid="eq-maint-cost" />
                        </div>
                        <div>
                            <Label className="text-xs">{i18n.t("final.2b4ed7018503")}</Label>
                            <Input type="number" value={draft.hours_at_service ?? ""} onChange={(e) => upd("hours_at_service", Number(e.target.value) || null)} className="mt-1 h-10" />
                        </div>
                        <div>
                            <Label className="text-xs">{i18n.t("final.71e7e5db4ac7")}</Label>
                            <Input type="date" value={draft.next_due_date || ""} onChange={(e) => upd("next_due_date", e.target.value)} className="mt-1 h-10" />
                        </div>
                        <div>
                            <Label className="text-xs">{i18n.t("final.c9f9a9698524")}</Label>
                            <Input type="number" value={draft.next_due_hours ?? ""} onChange={(e) => upd("next_due_hours", Number(e.target.value) || null)} className="mt-1 h-10" />
                        </div>
                    </div>
                    <div>
                        <Label className="text-xs">{i18n.t("final.7ceee3f3615a")}</Label>
                        <Input value={draft.provider ?? ""} onChange={(e) => upd("provider", e.target.value)} className="mt-1 h-10" />
                    </div>
                    <div>
                        <Label className="text-xs">{i18n.t("final.4bcd00d023b4")}</Label>
                        <Textarea rows={2} value={draft.notes ?? ""} onChange={(e) => upd("notes", e.target.value)} className="mt-1" />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>{i18n.t("final.77dfd2135f4d")}</Button>
                    <Button onClick={() => onSave(draft)} disabled={!draft.equipment_id} data-testid="eq-maint-save-btn">
                        <Save className="w-4 h-4 mr-2" />{i18n.t("final.efc007a393f6")}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// ---------- Spare Part Dialog ----------
function PartDialog({ open, initial, equipment, onSave, onClose }) {
    const [draft, setDraft] = useState({});
    useEffect(() => {
        if (open) setDraft({ quantity: 0, reorder_level: 0, unit: "pcs", source_submission_id: initial?.source_submission_id || submissionId("equipment-part"), ...(initial || {}) });
    }, [open, initial?.id]);
    const upd = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-lg" data-testid="part-dialog">
                <DialogHeader><DialogTitle className="flex items-center gap-2"><Package className="w-5 h-5" />{draft.id ? "Edit Part" : "New Spare Part"}</DialogTitle></DialogHeader>
                <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="text-xs">{i18n.t("final.709a23220f2c")}</Label>
                            <Input value={draft.name ?? ""} onChange={(e) => upd("name", e.target.value)} className="mt-1 h-10" data-testid="part-name" />
                        </div>
                        <div>
                            <Label className="text-xs">{i18n.t("final.dd8dbbd79e86")}</Label>
                            <Input value={draft.part_no ?? ""} onChange={(e) => upd("part_no", e.target.value)} className="mt-1 h-10" />
                        </div>
                        <div>
                            <Label className="text-xs">{i18n.t("final.a3c686e711e4")}</Label>
                            <Input value={draft.category ?? ""} onChange={(e) => upd("category", e.target.value)} placeholder={i18n.t("final.81259aa4c0b6")} className="mt-1 h-10" />
                        </div>
                        <div>
                            <Label className="text-xs">{i18n.t("final.0696fec6fead")}</Label>
                            <Select value={draft.equipment_id || "__shared__"} onValueChange={(v) => upd("equipment_id", v === "__shared__" ? null : v)}>
                                <SelectTrigger className="mt-1 h-10"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="__shared__">{i18n.t("final.a278a03b221f")}</SelectItem>
                                    {equipment.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-xs">{i18n.t("final.44f6af694554")}</Label>
                            <Input type="number" step="0.01" value={draft.quantity ?? 0} onChange={(e) => upd("quantity", Number(e.target.value) || 0)} className="mt-1 h-10" data-testid="part-qty" />
                        </div>
                        <div>
                            <Label className="text-xs">{i18n.t("final.f6b935ab3371")}</Label>
                            <Input value={draft.unit ?? "pcs"} onChange={(e) => upd("unit", e.target.value)} className="mt-1 h-10" />
                        </div>
                        <div>
                            <Label className="text-xs">{i18n.t("final.59ef31e37acc")}</Label>
                            <Input type="number" value={draft.reorder_level ?? 0} onChange={(e) => upd("reorder_level", Number(e.target.value) || 0)} className="mt-1 h-10" />
                        </div>
                        <div>
                            <Label className="text-xs">{i18n.t("final.98061c6fa8e2")}</Label>
                            <Input type="number" value={draft.unit_cost ?? 0} onChange={(e) => upd("unit_cost", Number(e.target.value) || 0)} className="mt-1 h-10" />
                        </div>
                        <div className="col-span-2">
                            <Label className="text-xs">{i18n.t("final.55edd462872a")}</Label>
                            <Input value={draft.supplier ?? ""} onChange={(e) => upd("supplier", e.target.value)} className="mt-1 h-10" />
                        </div>
                        <div className="col-span-2">
                            <Label className="text-xs">{i18n.t("final.a225c8ebcac0")}</Label>
                            <Input value={draft.location ?? ""} onChange={(e) => upd("location", e.target.value)} className="mt-1 h-10" />
                        </div>
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>{i18n.t("final.77dfd2135f4d")}</Button>
                    <Button onClick={() => onSave(draft)} disabled={!draft.name} data-testid="part-save-btn">
                        <Save className="w-4 h-4 mr-2" />{i18n.t("final.efc007a393f6")}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// ---------- Main Page ----------
export default function EquipmentPage() {
    const { t } = useTranslation();
    const { can, isOwner } = useAuth();
    const [tab, setTab] = useState("dashboard");
    const [summary, setSummary] = useState({ kpis: {}, hours_trend: [], service_upcoming: [], service_overdue: [], hours_alerts: [] });
    const [equipment, setEquipment] = useState([]);
    const [hoursLogs, setHoursLogs] = useState([]);
    const [maint, setMaint] = useState([]);
    const [parts, setParts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [inspectionTpls, setInspectionTpls] = useState([]);
    const [equipDlg, setEquipDlg] = useState(null);
    const [hoursDlg, setHoursDlg] = useState(false);
    const [maintDlg, setMaintDlg] = useState(false);
    const [partDlg, setPartDlg] = useState(null);
    const [newCatName, setNewCatName] = useState("");

    const empById = useMemo(() => Object.fromEntries(employees.map((e) => [e.id, e])), [employees]);

    const load = async () => {
        try {
            const [s, eq, h, m, p, c, emp, tpls] = await Promise.all([
                api.get("/equipment/summary"),
                api.get("/equipment"),
                api.get("/equipment/engine-hours?limit=100"),
                api.get("/equipment/maintenance?limit=100"),
                api.get("/equipment/spare-parts"),
                api.get("/equipment/categories"),
                api.get("/employees").catch(() => ({ data: [] })),
                api.get("/forms/templates?category=equipment_inspection"),
            ]);
            setSummary(s.data);
            setEquipment(eq.data || []);
            setHoursLogs(h.data || []);
            setMaint(m.data || []);
            setParts(p.data || []);
            setCategories(c.data || []);
            setEmployees(emp.data || []);
            setInspectionTpls(tpls.data || []);
        } catch (e) {
            toast.error("Failed to load equipment data");
        }
    };
    useEffect(() => { load(); }, []);

    const saveEquipment = async (v) => {
        try {
            const payload = { ...v };
            // ensure type is one of the equipment types (not vehicle, not building)
            if (!EQ_TYPES.includes(payload.type)) payload.type = "machinery";
            if (v.id) await api.put(`/farm-assets/${v.id}`, payload);
            else await api.post("/farm-assets", payload);
            toast.success("Equipment saved");
            setEquipDlg(null);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        }
    };
    const delEquipment = async (e) => {
        if (!confirm(`Delete ${e.name}?`)) return;
        try { await api.delete(`/farm-assets/${e.id}`); toast.success("Deleted"); load(); }
        catch { toast.error("Delete failed"); }
    };
    const saveHours = async (d) => {
        try { await api.post("/equipment/engine-hours", d); toast.success("Hours logged"); setHoursDlg(false); load(); }
        catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    };
    const delHours = async (r) => {
        if (!confirm("Delete this log?")) return;
        try { await api.delete(`/equipment/engine-hours/${r.id}`); toast.success("Deleted"); load(); }
        catch { toast.error("Delete failed"); }
    };
    const saveMaint = async (d) => {
        try { await api.post("/equipment/maintenance", d); toast.success("Service saved"); setMaintDlg(false); load(); }
        catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    };
    const delMaint = async (r) => {
        if (!confirm("Delete this record?")) return;
        try { await api.delete(`/equipment/maintenance/${r.id}`); toast.success("Deleted"); load(); }
        catch { toast.error("Delete failed"); }
    };
    const savePart = async (d) => {
        try {
            if (d.id) await api.put(`/equipment/spare-parts/${d.id}`, d);
            else await api.post("/equipment/spare-parts", d);
            toast.success("Part saved");
            setPartDlg(null);
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    };
    const adjustPart = async (p, delta) => {
        try { await api.post(`/equipment/spare-parts/${p.id}/adjust`, { delta, reason: delta > 0 ? "manual +" : "manual -" }); load(); }
        catch { toast.error("Adjust failed"); }
    };
    const delPart = async (p) => {
        if (!confirm(`Delete ${p.name}?`)) return;
        try { await api.delete(`/equipment/spare-parts/${p.id}`); toast.success("Deleted"); load(); }
        catch { toast.error("Delete failed"); }
    };
    const addCategory = async () => {
        const name = newCatName.trim();
        if (!name) return;
        try { await api.post("/equipment/categories", { name }); toast.success("Category added"); setNewCatName(""); load(); }
        catch (e) { toast.error(e?.response?.data?.detail || "Add failed"); }
    };

    const k = summary.kpis || {};
    const health = k.equipment_health ?? 100;
    const healthTone = health >= 85 ? "emerald" : health >= 60 ? "amber" : "rose";

    return (
        <div className="space-y-6" data-testid="equipment-page">
            <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                    <h1 className="text-3xl sm:text-4xl font-bold flex items-center gap-3">
                        <Cog className="w-8 h-8 text-primary" />
                        {t("nav.equipment", "Machinery & Equipment")}
                    </h1>
                    <p className="text-muted-foreground mt-1 text-sm">
                        {t("equipment.subtitle", "Tractors · pumps · generators · tools · engine-hour tracking · spare parts · inspections.")}
                    </p>
                </div>
                <div className="flex gap-2">
                    {can("add") && (
                        <>
                            <Button variant="outline" onClick={() => setHoursDlg(true)} data-testid="new-hours-btn">
                                <Timer className="w-4 h-4 mr-2" />{i18n.t("final.9c0048f2d882")}
                            </Button>
                            <Button variant="outline" onClick={() => setMaintDlg(true)} data-testid="new-eq-maint-btn">
                                <Wrench className="w-4 h-4 mr-2" />{i18n.t("final.9497c416e33c")}
                            </Button>
                            <Button onClick={() => setEquipDlg({})} data-testid="new-equipment-btn">
                                <Plus className="w-4 h-4 mr-2" />{i18n.t("final.23865ea0f0a2")}
                            </Button>
                        </>
                    )}
                </div>
            </div>

            <Tabs value={tab} onValueChange={setTab}>
                <TabsList className="flex-wrap">
                    <TabsTrigger value="dashboard" data-testid="tab-dashboard">{i18n.t("final.d87f47b47e4d")}</TabsTrigger>
                    <TabsTrigger value="registry" data-testid="tab-registry">
                        {i18n.t("final.1fd6a805dae7")} <Badge variant="secondary" className="ml-2 text-[10px]">{equipment.length}</Badge>
                    </TabsTrigger>
                    <TabsTrigger value="hours" data-testid="tab-hours">
                        {i18n.t("final.2942e6ae9dc3")} <Badge variant="secondary" className="ml-2 text-[10px]">{hoursLogs.length}</Badge>
                    </TabsTrigger>
                    <TabsTrigger value="maintenance" data-testid="tab-eq-maint">
                        {i18n.t("final.94de303bbef8")} <Badge variant="secondary" className="ml-2 text-[10px]">{maint.length}</Badge>
                    </TabsTrigger>
                    <TabsTrigger value="parts" data-testid="tab-parts">
                        {i18n.t("final.2d3141877a3e")} <Badge variant="secondary" className="ml-2 text-[10px]">{parts.length}</Badge>
                    </TabsTrigger>
                    <TabsTrigger value="categories" data-testid="tab-categories">
                        {i18n.t("final.6ccb60071be8")} <Badge variant="secondary" className="ml-2 text-[10px]">{categories.length}</Badge>
                    </TabsTrigger>
                    <TabsTrigger value="inspections" data-testid="tab-eq-inspections">{i18n.t("final.0d7c240cba1c")}</TabsTrigger>
                </TabsList>

                {/* DASHBOARD */}
                <TabsContent value="dashboard" className="mt-4 space-y-5">
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                        <KpiCard icon={Cog} label={i18n.t("final.e8f88ca7278e")} value={formatNumber(k.total_equipment)} tone="primary" testid="kpi-eq-total" />
                        <KpiCard icon={Activity} label={i18n.t("final.6b12d06ba166")} value={formatNumber(k.operating)} tone="blue" testid="kpi-eq-op" />
                        <KpiCard icon={Gauge} label={i18n.t("final.cc1ebdd04e76")} value={formatNumber(k.idle)} tone="slate" testid="kpi-eq-idle" />
                        <KpiCard icon={Wrench} label={i18n.t("final.cd368978369a")} value={formatNumber(k.in_repair)} tone="amber" testid="kpi-eq-repair" />
                        <KpiCard icon={Gauge} label={i18n.t("final.6036f38949f0")} value={`${health}%`} tone={healthTone} testid="kpi-eq-health" />
                        <KpiCard icon={Boxes} label={i18n.t("final.6ccb60071be8")} value={formatNumber(k.categories_count)} tone="slate" testid="kpi-eq-cats" />
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                        <KpiCard icon={Timer} label={i18n.t("final.2942e6ae9dc3")} value={formatNumber(k.total_engine_hours)} sub="Total fleet" tone="primary" testid="kpi-eq-hours" />
                        <KpiCard icon={AlertTriangle} label={i18n.t("final.53a849c428ba")} value={formatNumber(k.hours_alerts)}
                                 tone={k.hours_alerts > 0 ? "rose" : "slate"} sub="Near next service" testid="kpi-eq-hrs-alert" />
                        <KpiCard icon={Wrench} label={i18n.t("final.c088d79f1373")} value={formatNumber(k.service_upcoming)}
                                 tone={k.service_upcoming > 0 ? "amber" : "slate"} sub="Next 30 days" testid="kpi-eq-svc-up" />
                        <KpiCard icon={AlertTriangle} label={i18n.t("final.166240377046")} value={formatNumber(k.service_overdue)}
                                 tone={k.service_overdue > 0 ? "rose" : "slate"} testid="kpi-eq-svc-overdue" />
                        <KpiCard icon={Package} label={i18n.t("final.2d3141877a3e")} value={formatNumber(k.spare_parts_total)}
                                 sub={`${k.spare_parts_low || 0} low`} tone={k.spare_parts_low > 0 ? "amber" : "primary"} testid="kpi-eq-parts" />
                        <KpiCard icon={Gauge} label={i18n.t("final.ea3c24ad2473")} value={`${k.utilization || 0}%`} tone="blue"
                                 sub="Logged hours this month" testid="kpi-eq-util" />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <KpiCard icon={Wrench} label={i18n.t("final.c628afb091a6")} value={formatCurrency(k.maintenance_month || 0)} tone="amber" testid="kpi-eq-maint-mo" />
                        <KpiCard icon={Wrench} label={i18n.t("final.5612a12912ba")} value={formatCurrency(k.repair_month || 0)} tone="rose" testid="kpi-eq-repair-mo" />
                        <KpiCard icon={Activity} label={i18n.t("final.65c2337e5a7b")} value={formatNumber(k.assigned)}
                                 sub={`${k.unassigned || 0} unassigned`} tone="slate" testid="kpi-eq-assigned" />
                    </div>

                    <Card className="p-5">
                        <div className="font-semibold mb-3 flex items-center gap-2"><Timer className="w-4 h-4" />{i18n.t("final.2144d9aeea32")}</div>
                        {(summary.hours_trend || []).length === 0 ? (
                            <div className="text-sm text-muted-foreground">{i18n.t("final.a38fbb777345")}</div>
                        ) : (
                            <div className="h-56">
                                <ResponsiveContainer>
                                    <LineChart data={summary.hours_trend}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                                        <XAxis dataKey="date" fontSize={10} />
                                        <YAxis fontSize={10} />
                                        <Tooltip />
                                        <Line type="monotone" dataKey="hours" stroke="hsl(var(--primary))" strokeWidth={2} name="Hours" dot={false} />
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                        )}
                    </Card>

                    {(summary.hours_alerts?.length || summary.service_overdue?.length || summary.service_upcoming?.length) ? (
                        <Card className="p-5">
                            <div className="font-semibold mb-3 flex items-center gap-2"><AlertTriangle className="w-4 h-4" />{i18n.t("final.1c5855ed9335")}</div>
                            <div className="grid md:grid-cols-3 gap-4 text-sm">
                                <div>
                                    <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">{i18n.t("final.07217c77199f")}</div>
                                    {(summary.service_overdue || []).length === 0 ? <div className="text-muted-foreground text-xs">{i18n.t("final.6eef6648406c")}</div> :
                                     summary.service_overdue.map((r, i) => (
                                        <div key={i} className="text-xs text-rose-600 py-1"><b>{r.name}</b> {i18n.t("final.cb0538e4ad77")} {r.days_overdue}{i18n.t("final.3c363836cf4e")}</div>
                                     ))}
                                </div>
                                <div>
                                    <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">{i18n.t("final.a4ec6579de8a")}</div>
                                    {(summary.service_upcoming || []).length === 0 ? <div className="text-muted-foreground text-xs">{i18n.t("final.6eef6648406c")}</div> :
                                     summary.service_upcoming.map((r, i) => (
                                        <div key={i} className="text-xs py-1"><b>{r.name}</b> {i18n.t("final.1131d8224bf0")} {r.days}{i18n.t("final.5635a7fdb9c2")}{r.date})</div>
                                     ))}
                                </div>
                                <div>
                                    <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">{i18n.t("final.cec5fc32e25c")}</div>
                                    {(summary.hours_alerts || []).length === 0 ? <div className="text-muted-foreground text-xs">{i18n.t("final.6eef6648406c")}</div> :
                                     summary.hours_alerts.map((r, i) => (
                                        <div key={i} className={`text-xs py-1 ${r.hours_remaining <= 0 ? "text-rose-600" : ""}`}>
                                            <b>{r.name}</b> · {r.hours_remaining <= 0 ? `overdue by ${-r.hours_remaining}h` : `${r.hours_remaining}h left`}
                                        </div>
                                     ))}
                                </div>
                            </div>
                        </Card>
                    ) : null}
                </TabsContent>

                {/* REGISTRY */}
                <TabsContent value="registry" className="mt-4">
                    {equipment.length === 0 ? (
                        <Card className="p-8 text-center text-muted-foreground">
                            {i18n.t("final.77777ca55ede")} <b>{i18n.t("final.23865ea0f0a2")}</b> {i18n.t("final.65f495119086")}
                        </Card>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {equipment.map((e) => {
                                const s = statusMeta(e.status);
                                return (
                                    <Card key={e.id} className="p-4" data-testid={`equip-card-${e.id}`}>
                                        <div className="flex justify-between items-start gap-2">
                                            <div className="flex-1 min-w-0">
                                                <div className="font-semibold text-lg leading-tight truncate">{e.name}</div>
                                                <div className="text-xs text-muted-foreground mt-0.5 truncate">
                                                    {e.category || "Uncategorized"}
                                                    {e.brand ? ` · ${e.brand}` : ""}
                                                    {e.model ? ` ${e.model}` : ""}
                                                </div>
                                            </div>
                                            <Badge className={`text-[10px] ${s.color}`} variant="outline">{s.label}</Badge>
                                        </div>
                                        <div className="grid grid-cols-2 gap-2 mt-3 text-xs">
                                            {e.assigned_operator_id && empById[e.assigned_operator_id] && (
                                                <div className="col-span-2 text-muted-foreground">
                                                    {i18n.t("final.084fe2b79161")} <b className="text-foreground">{empById[e.assigned_operator_id].name}</b>
                                                </div>
                                            )}
                                            {e.engine_hours != null && (
                                                <div className="text-muted-foreground">{i18n.t("final.5c0a29f837f1")} <b className="text-foreground">{formatNumber(e.engine_hours)}{i18n.t("final.27d5482eebd0")}</b></div>
                                            )}
                                            {e.next_due_hours != null && e.engine_hours != null && (
                                                <div className={`${(e.next_due_hours - e.engine_hours) <= 20 ? "text-rose-600" : "text-muted-foreground"}`}>
                                                    {i18n.t("final.69a52c6de668")} {Math.max(0, e.next_due_hours - e.engine_hours).toFixed(0)}{i18n.t("final.27d5482eebd0")}
                                                </div>
                                            )}
                                            {e.next_service_date && (
                                                <div className="col-span-2 text-muted-foreground">{i18n.t("final.7ffef0952c80")} {e.next_service_date}</div>
                                            )}
                                            {e.location && (
                                                <div className="col-span-2 text-muted-foreground">{i18n.t("final.0aa8ae04295f")} {e.location}</div>
                                            )}
                                        </div>
                                        {can("edit") && (
                                            <div className="mt-3 flex gap-2">
                                                <Button size="sm" variant="outline" onClick={() => setEquipDlg(e)} data-testid={`edit-equip-${e.id}`}>
                                                    <PencilLine className="w-3.5 h-3.5 mr-1" />{i18n.t("final.5301648dcf6b")}
                                                </Button>
                                                {can("delete") && (
                                                    <Button size="sm" variant="ghost" onClick={() => delEquipment(e)}>
                                                        <Trash2 className="w-3.5 h-3.5 text-rose-500" />
                                                    </Button>
                                                )}
                                            </div>
                                        )}
                                    </Card>
                                );
                            })}
                        </div>
                    )}
                </TabsContent>

                {/* ENGINE HOURS */}
                <TabsContent value="hours" className="mt-4">
                    {hoursLogs.length === 0 ? (
                        <Card className="p-8 text-center text-muted-foreground">{i18n.t("final.18faa35def10")}</Card>
                    ) : (
                        <Card>
                            <div className="p-4 border-b flex items-center justify-between">
                                <div className="font-semibold">{i18n.t("final.276652d8d16b")}</div>
                                <Button size="sm" onClick={() => setHoursDlg(true)}><Plus className="w-4 h-4 mr-1" />{i18n.t("final.9c0048f2d882")}</Button>
                            </div>
                            <div className="divide-y">
                                {hoursLogs.map((h) => {
                                    const e = equipment.find((x) => x.id === h.equipment_id);
                                    return (
                                        <div key={h.id} className="p-4 flex items-center gap-3 text-sm" data-testid={`hours-row-${h.id}`}>
                                            <div className="w-9 h-9 rounded-lg bg-primary/10 text-primary grid place-items-center">
                                                <Timer className="w-4 h-4" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="font-semibold truncate">{e?.name || "?"}</div>
                                                <div className="text-xs text-muted-foreground">
                                                    {h.date} {i18n.t("final.da7be9accee6")} {h.hours_reading}{i18n.t("final.27d5482eebd0")}
                                                    {h.delta !== null && h.delta !== undefined ? ` · +${h.delta}h since last` : ""}
                                                </div>
                                            </div>
                                            {can("delete") && (
                                                <Button size="sm" variant="ghost" onClick={() => delHours(h)}>
                                                    <Trash2 className="w-3.5 h-3.5 text-rose-500" />
                                                </Button>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </Card>
                    )}
                </TabsContent>

                {/* MAINTENANCE */}
                <TabsContent value="maintenance" className="mt-4">
                    {maint.length === 0 ? (
                        <Card className="p-8 text-center text-muted-foreground">{i18n.t("final.4efaca55bbdd")}</Card>
                    ) : (
                        <Card>
                            <div className="p-4 border-b flex items-center justify-between">
                                <div className="font-semibold">{i18n.t("final.c9b5eabe92c4")}</div>
                                <Button size="sm" onClick={() => setMaintDlg(true)}><Plus className="w-4 h-4 mr-1" />{i18n.t("final.9497c416e33c")}</Button>
                            </div>
                            <div className="divide-y">
                                {maint.map((m) => {
                                    const e = equipment.find((x) => x.id === m.equipment_id);
                                    return (
                                        <div key={m.id} className="p-4 flex items-center gap-3 text-sm" data-testid={`eq-maint-row-${m.id}`}>
                                            <div className="w-9 h-9 rounded-lg bg-amber-500/15 text-amber-700 grid place-items-center">
                                                <Wrench className="w-4 h-4" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="font-semibold truncate">
                                                    {e?.name || "?"} <span className="text-xs font-normal text-muted-foreground">· {m.kind}</span>
                                                </div>
                                                <div className="text-xs text-muted-foreground">
                                                    {m.date}
                                                    {m.provider ? ` · ${m.provider}` : ""}
                                                    {m.hours_at_service != null ? ` · @ ${m.hours_at_service}h` : ""}
                                                    {m.next_due_date ? ` · next: ${m.next_due_date}` : ""}
                                                    {m.next_due_hours ? ` · next @ ${m.next_due_hours}h` : ""}
                                                </div>
                                                {m.notes && <div className="text-xs mt-1 text-muted-foreground truncate">{m.notes}</div>}
                                            </div>
                                            <div className="text-right">
                                                <div className="font-semibold">{formatCurrency(m.cost || 0)}</div>
                                                {can("delete") && (
                                                    <Button size="sm" variant="ghost" onClick={() => delMaint(m)}>
                                                        <Trash2 className="w-3.5 h-3.5 text-rose-500" />
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </Card>
                    )}
                </TabsContent>

                {/* SPARE PARTS */}
                <TabsContent value="parts" className="mt-4">
                    <div className="flex justify-end mb-3">
                        <Button size="sm" onClick={() => setPartDlg({})} data-testid="new-part-btn">
                            <Plus className="w-4 h-4 mr-1" />{i18n.t("final.83760b6974e1")}
                        </Button>
                    </div>
                    {parts.length === 0 ? (
                        <Card className="p-8 text-center text-muted-foreground">{i18n.t("final.4755bb542fc4")}</Card>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                            {parts.map((p) => {
                                const low = Number(p.quantity || 0) <= Number(p.reorder_level || 0);
                                const fit = equipment.find((e) => e.id === p.equipment_id);
                                return (
                                    <Card key={p.id} className={`p-4 ${low ? "border-amber-500/40" : ""}`} data-testid={`part-card-${p.id}`}>
                                        <div className="flex justify-between items-start gap-2">
                                            <div className="flex-1 min-w-0">
                                                <div className="font-semibold truncate">{p.name}</div>
                                                <div className="text-xs text-muted-foreground mt-0.5 truncate">
                                                    {p.part_no ? p.part_no : "no part #"}
                                                    {p.category ? ` · ${p.category}` : ""}
                                                </div>
                                                <div className="text-xs text-muted-foreground mt-1">
                                                    {fit ? `Fits: ${fit.name}` : "Shared pool"}
                                                </div>
                                            </div>
                                            {low && <Badge variant="outline" className="text-[10px] bg-amber-500/15 text-amber-700">{i18n.t("final.a124947cbd2d")}</Badge>}
                                        </div>
                                        <div className="mt-3 flex items-center justify-between">
                                            <div>
                                                <div className="text-xl font-bold" data-testid={`part-qty-${p.id}`}>{p.quantity} <span className="text-xs font-normal text-muted-foreground">{p.unit}</span></div>
                                                <div className="text-[11px] text-muted-foreground">{i18n.t("final.6723e47bb201")} {p.reorder_level || 0}</div>
                                            </div>
                                            {can("edit") && (
                                                <div className="flex gap-1">
                                                    <Button size="sm" variant="outline" onClick={() => adjustPart(p, -1)} data-testid={`part-minus-${p.id}`}>−</Button>
                                                    <Button size="sm" variant="outline" onClick={() => adjustPart(p, 1)} data-testid={`part-plus-${p.id}`}>+</Button>
                                                </div>
                                            )}
                                        </div>
                                        {can("edit") && (
                                            <div className="mt-3 flex gap-2">
                                                <Button size="sm" variant="outline" onClick={() => setPartDlg(p)}>
                                                    <PencilLine className="w-3.5 h-3.5 mr-1" />{i18n.t("final.5301648dcf6b")}
                                                </Button>
                                                {can("delete") && (
                                                    <Button size="sm" variant="ghost" onClick={() => delPart(p)}>
                                                        <Trash2 className="w-3.5 h-3.5 text-rose-500" />
                                                    </Button>
                                                )}
                                            </div>
                                        )}
                                    </Card>
                                );
                            })}
                        </div>
                    )}
                </TabsContent>

                {/* CATEGORIES */}
                <TabsContent value="categories" className="mt-4">
                    <Card className="p-5 space-y-3">
                        <div className="font-semibold">{i18n.t("final.c3290bda1143")}</div>
                        <div className="text-sm text-muted-foreground">
                            {i18n.t("final.5c7f68126c6b")}
                        </div>
                        {isOwner() && (
                            <div className="flex gap-2">
                                <Input value={newCatName} onChange={(e) => setNewCatName(e.target.value)}
                                       placeholder={i18n.t("final.b8f5ffa03eb9")} className="h-10" data-testid="new-cat-name" />
                                <Button onClick={addCategory} data-testid="add-cat-btn">
                                    <Plus className="w-4 h-4 mr-1" />{i18n.t("final.61cc55aa0453")}
                                </Button>
                            </div>
                        )}
                        <div className="flex flex-wrap gap-2 pt-2">
                            {categories.map((c) => (
                                <Badge key={c.name} variant={c.custom ? "default" : "outline"} className="text-xs" data-testid={`cat-${c.name}`}>
                                    {c.name}
                                    {c.custom && !c.is_default && isOwner() && (
                                        <button className="ml-2 text-rose-500" onClick={async () => {
                                            if (confirm(`Delete category "${c.name}"?`)) {
                                                await api.delete(`/equipment/categories/${encodeURIComponent(c.name)}`);
                                                load();
                                            }
                                        }}>
                                            ×
                                        </button>
                                    )}
                                </Badge>
                            ))}
                        </div>
                    </Card>
                </TabsContent>

                {/* INSPECTIONS */}
                <TabsContent value="inspections" className="mt-4">
                    <Card className="p-5 space-y-3">
                        <div className="flex items-center gap-2 font-semibold">
                            <ClipboardCheck className="w-4 h-4" />{i18n.t("final.1706df6036ce")}
                        </div>
                        <div className="text-sm text-muted-foreground">
                            {i18n.t("final.13537cab231b")} <b>{i18n.t("final.8f7de73e0745")}</b>{i18n.t("final.6e0f112a6c87")}
                        </div>
                        <div className="grid md:grid-cols-2 gap-3">
                            {inspectionTpls.map((tpl) => (
                                <Card key={tpl.id} className="p-4" data-testid={`eq-insp-tpl-${tpl.code}`}>
                                    <div className="font-semibold">{tpl.title}</div>
                                    <div className="text-xs text-muted-foreground mt-1">{tpl.description}</div>
                                    <div className="text-xs text-muted-foreground mt-2">{(tpl.fields || []).length} {i18n.t("final.70f132fe13e6")}</div>
                                    <Button asChild size="sm" className="mt-3">
                                        <a href="/forms">{i18n.t("final.f9543f047f25")}</a>
                                    </Button>
                                </Card>
                            ))}
                        </div>
                    </Card>
                </TabsContent>
            </Tabs>

            <EquipmentDialog
                open={!!equipDlg}
                initial={equipDlg}
                categories={categories}
                employees={employees}
                onSave={saveEquipment}
                onClose={() => setEquipDlg(null)}
            />
            <HoursDialog open={hoursDlg} equipment={equipment} onSave={saveHours} onClose={() => setHoursDlg(false)} />
            <MaintDialog open={maintDlg} equipment={equipment} onSave={saveMaint} onClose={() => setMaintDlg(false)} />
            <PartDialog open={!!partDlg} initial={partDlg} equipment={equipment} onSave={savePart} onClose={() => setPartDlg(null)} />
        </div>
    );
}
